// BlogDetails.js
import React, { useEffect, useState } from 'react';
import { Link, /* useNavigate, */ useParams } from 'react-router-dom'; // useNavigate is not used
import { collection, query, where, getDocs } from 'firebase/firestore';
import { textdb, /* auth */ } from '../../firebase'; // auth is not used
import DOMPurify from 'dompurify';
import styles from "./BlogDetails.module.css"; // Import as styles object
import { useCollection } from 'react-firebase-hooks/firestore';
import { CiCalendarDate } from "react-icons/ci";
import { Helmet } from 'react-helmet';
import Load from '../Load/Load';

// Helper function to generate a slug from a title (ensure consistency with upload)
const generateSlug = (text) => {
  if (!text) return '';
  return text
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-+|-+$/g, '');
};

export const BlogDetails = () => {
  const [allBlogs, loadingOtherBlogs, errorOtherBlogs] = useCollection(collection(textdb, 'Blogs'));
  const { blogId } = useParams();

  const [blogData, setBlogData] = useState(null);
  const [isLoadingBlog, setIsLoadingBlog] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const fetchBlogDetails = async () => {
      setIsLoadingBlog(true);
      setNotFound(false);
      setBlogData(null);

      if (!blogId) {
          setNotFound(true);
          setIsLoadingBlog(false);
          return;
      }

      try {
        const decodedSlug = decodeURIComponent(blogId).trim();
        console.log("Fetching blog with slug:", decodedSlug);

        const q = query(
          collection(textdb, 'Blogs'),
          where('slug', '==', decodedSlug)
        );
        const querySnapshot = await getDocs(q);

        if (querySnapshot.empty) {
          console.log('No document found for the given slug!');
          setNotFound(true);
        } else {
          const docData = querySnapshot.docs[0].data();
          setBlogData({ id: querySnapshot.docs[0].id, ...docData });
        }
      } catch (error) {
        console.error('Error fetching blog details:', error);
        setNotFound(true);
      } finally {
        setIsLoadingBlog(false);
      }
    };

    fetchBlogDetails();
  }, [blogId]);

  if (isLoadingBlog) {
    return <Load />;
  }

  if (notFound) {
    return (
      <div className={styles['blog-detail-container']}> {/* Access class with bracket notation */}
        <div className={styles['blog-detail-not-found']}>
          <h1>404</h1>
          <p>Blog post not found.</p>
          <Link to="/blog" className={styles['blog-detail-button']}>Back to Blog</Link>
        </div>
      </div>
    );
  }
  
  if (!blogData) {
    return <Load />;
  }

  const otherBlogPosts = allBlogs?.docs.filter(doc => doc.id !== blogData.id) || [];

  return (
    <div className={styles['blog-detail-container']}>
      <Helmet>
        <meta charSet="utf-8" />
        <title>{blogData.title ? `${blogData.title} | Our Blog` : 'Blog Post'}</title>
        <meta name="description" content={blogData.description || "Read our latest blog post."} />
      </Helmet>

      <div className={styles['blog-detail-hero']}>
        <nav className={styles['blog-detail-breadcrumb']}>
          <Link to="/" className={styles['blog-detail-breadcrumb__link']}>Home</Link>
          <span className={styles['blog-detail-breadcrumb__separator']}>></span> {/* Using > directly */}
          <Link to="/blog" className={styles['blog-detail-breadcrumb__link']}>Blog</Link>
          <span className={styles['blog-detail-breadcrumb__separator']}>></span> {/* Using > directly */}
          <span className={styles['blog-detail-breadcrumb__active']}>{blogData.title}</span>
        </nav>
        <h1 className={styles['blog-detail-hero__title']}>{blogData.title}</h1>
        <div className={styles['blog-detail-hero__meta']}>
          {blogData.author && (
            <span className={styles['blog-detail-hero__author']}>By {blogData.author}</span>
          )}
          {blogData.timestamp && (
            <span className={styles['blog-detail-hero__date']}>
              <CiCalendarDate />
              {new Date(blogData.timestamp.toDate()).toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'long',
                day: 'numeric',
              })}
            </span>
          )}
        </div>
      </div>

      <div className={styles['blog-detail-layout']}>
        <main className={styles['blog-detail-main-content']}>
          {blogData.iconImageUrl && (
            <div className={styles['blog-detail-main-content__image-container']}>
              <img
                loading="lazy"
                src={blogData.iconImageUrl}
                alt={blogData.title || 'Blog post feature image'}
                className={styles['blog-detail-main-content__image']}
              />
            </div>
          )}
          <article
            // For dynamically inserted HTML, you cannot directly apply module styles.
            // The styles for .blog-detail-main-content__body h1, p, a, etc.
            // will need to remain global or be handled differently (e.g., wrapping the article
            // in a div with a module class and then targeting children globally, or
            // processing the HTML content to add module classes - which is complex).
            // For now, we assume these inner styles are handled by the module targeting 
            // the parent `blog-detail-main-content__body` and its descendants.
            className={styles['blog-detail-main-content__body']}
            dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(blogData.content) }}
          />
        </main>

        {otherBlogPosts.length > 0 && (
        <aside className={styles['blog-detail-sidebar']}>
          <h2 className={styles['blog-detail-sidebar__title']}>Continue Reading</h2>
          <div className={styles['blog-detail-sidebar__list']}>
            {loadingOtherBlogs && <p className={styles['blog-detail-sidebar__loading']}>Loading posts...</p>}
            {errorOtherBlogs && <p className={styles['blog-detail-sidebar__error']}>Error loading posts.</p>}
            {otherBlogPosts.map((docItem) => {
                const data = docItem.data();
                if (!data || !data.title) return null;
                const slug = data.slug ? data.slug : generateSlug(data.title);
                if (!slug) return null;

                return (
                  <Link
                    key={docItem.id}
                    to={`/blog/${slug}`}
                    className={styles['blog-detail-sidebar-card-link']}
                  >
                    <div className={styles['blog-detail-sidebar-card']}>
                      {data.iconImageUrl && (
                        <img
                          className={styles['blog-detail-sidebar-card__image']}
                          src={data.iconImageUrl}
                          alt={data.title}
                          loading="lazy"
                        />
                      )}
                      <div className={styles['blog-detail-sidebar-card__content']}>
                        <h3 className={styles['blog-detail-sidebar-card__title']}>{data.title}</h3>
                        {data.timestamp && (
                           <p className={styles['blog-detail-sidebar-card__date']}>
                            <CiCalendarDate />
                            {new Date(data.timestamp.toDate()).toLocaleDateString('en-US', {
                              month: 'short',
                              day: 'numeric',
                              year: 'numeric',
                            })}
                          </p>
                        )}
                      </div>
                    </div>
                  </Link>
                );
              })}
          </div>
        </aside>
        )}
      </div>
    </div>
  );
};